import {
    emailReminderMessagePlaceholder,
    customReminderLabel,
    sendToAllAttendees,
    saveEmailRemidnerText,
    dicardEmailReminderText,
} from './EmailReminderEdit.locstring.json';
import { remindMe } from 'owa-locstrings/lib/strings/remindme.locstring.json';
import { emailReminderHeader, remindAllAttendeesLabel } from '../strings.locstring.json';
import loc from 'owa-localize';
import CustomReminderDatePickers from './CustomReminderDatePickers';
import { setCustomReminderDate } from '../actions/customReminderDateActions';
import getCustomReminderDate from '../selectors/getCustomReminderDate';
import getInboxReminderDraft from '../selectors/getInboxReminderDraft';
import EmailReminderEntrySource from '../store/schema/EmailReminderEntrySource';
import {
    getCustomReminderDisplayString,
    isReminderOffsetInDefaultOptions,
} from '../utils/getDisplayTextFromReminderOffset';
import isSendOptionToAllAttendees from '../utils/isSendOptionToAllAttendees';
import { observer } from 'mobx-react';
import { TextField } from 'office-ui-fabric-react/lib';
import { DefaultButton, PrimaryButton } from 'office-ui-fabric-react/lib/Button';
import { Checkbox } from 'office-ui-fabric-react/lib/Checkbox';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { CalendarIcons } from 'owa-calendar-icons';
import ReminderOptionType from 'owa-calendar-reminder-options/lib/helpers/ReminderOptionType';
import createReminderDropdownOptions from 'owa-calendar-reminder-options/lib/utils/createReminderDropdownOptions';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import generateDefaultDropdownStyles from 'owa-component-styling-utils/lib/utils/colorSchemesToStyles/generateDefaultDropdownStyles';
import generateDropdownCalloutStyles from 'owa-component-styling-utils/lib/utils/generateDropdownCalloutStyles';
import AttendeeType from 'owa-service/lib/contract/AttendeeType';
import EmailReminderSendOption from 'owa-service/lib/contract/EmailReminderSendOption';

import * as React from 'react';
import {
    Dropdown,
    IDropdownOption,
    DropdownMenuItemType,
} from 'office-ui-fabric-react/lib/Dropdown';
import {
    discardDraftInboxReminder,
    saveDraftInboxReminder,
    setDraftInboxReminderMessage,
    setDraftInboxReminderOffset,
    setDraftInboxReminderSendOption,
} from '../actions/draftInboxReminderActions';

const styles = require('./EmailReminderRow.scss');

export interface EmailReminderEditProps {
    event: CalendarEvent;
    colorScheme: ColorScheme;
    entrySource: EmailReminderEntrySource;
    isReminderNew: boolean;
}

@observer
export default class EmailReminderEdit extends React.Component<EmailReminderEditProps, {}> {
    private CUSTOM_EMAIL_REMINDER_KEY = 'custom';

    render() {
        return (
            <div className={styles.emailReminderRow}>
                {this.renderFirstRow()}
                {this.renderMessageTextInput()}
                {this.shouldRenderSendToAllAttendees() && this.renderSendOptionSection()}
                {this.renderSaveDiscardButtons()}
            </div>
        );
    }

    private renderFirstRow = (): JSX.Element => {
        return (
            <div className={styles.firstRow}>
                <Icon
                    className={styles.reminderIcon}
                    iconName={CalendarIcons.MailReminder}
                    title={loc(emailReminderHeader)}
                />
                <div className={styles.dropdownContainer}>
                    <Dropdown
                        calloutProps={{ styles: generateDropdownCalloutStyles(), gapSpace: 5 }}
                        label={this.getDropdownLabel()}
                        onChange={this.onDropdownOptionChange}
                        onRenderTitle={this.onRenderTitle}
                        options={this.createMenuOptions()}
                        selectedKey={this.getSelectedKey()}
                        styles={generateDefaultDropdownStyles(this.props.colorScheme)}
                    />
                </div>
                {this.shouldShowCustomReminderDatePickers() && <CustomReminderDatePickers />}
            </div>
        );
    };

    private onRenderTitle = (options: IDropdownOption[]): JSX.Element => {
        let textToDisplay = options[0].text;

        // If the editted reminder is an existing reminder and if reminder has a custom reminder date, convert to display text and show it
        // Otherwise for new reminder, or existing reminder which uses default option, just display the default dropdown selected text, whih is the selected reminder options's text
        if (!this.props.isReminderNew) {
            const reminderOffset = getInboxReminderDraft().ReminderOffset;
            const isCustomReminderOffset = !isReminderOffsetInDefaultOptions(
                reminderOffset,
                this.props.event.IsAllDayEvent
            );
            if (isCustomReminderOffset) {
                textToDisplay = getCustomReminderDisplayString(
                    reminderOffset,
                    this.props.event.Start
                );
            }
        }

        return <div>{textToDisplay}</div>;
    };

    private getDropdownLabel(): string {
        return isSendOptionToAllAttendees(getInboxReminderDraft().SendOption)
            ? loc(remindAllAttendeesLabel)
            : loc(remindMe);
    }

    private renderMessageTextInput = (): JSX.Element => {
        return (
            <TextField
                styles={{
                    wrapper: {
                        width: '100%',
                    },
                }}
                className={styles.textInput}
                multiline={true}
                onChange={this.onReminderMessageChanged}
                placeholder={loc(emailReminderMessagePlaceholder)}
                resizable={true}
                value={getInboxReminderDraft().Message}
            />
        );
    };

    private getSelectedKey = (): number | string => {
        if (this.shouldShowCustomReminderDatePickers()) {
            return this.CUSTOM_EMAIL_REMINDER_KEY;
        } else {
            // The selected reminder time in minutes of the inbox reminder which is being editted
            return getInboxReminderDraft().ReminderOffset;
        }
    };

    /**
     *  Create the options for the reminder dropdown
     *  @return options of type IDropdownOption[]
     */
    private createMenuOptions = (): IDropdownOption[] => {
        const draftReminder = getInboxReminderDraft();
        const reminderDropdownOptions: ReminderOptionType[] = createReminderDropdownOptions(
            this.props.event.IsAllDayEvent,
            draftReminder.ReminderOffset,
            false /* includeNoReminder */
        );

        const options: IDropdownOption[] = reminderDropdownOptions.map(reminder => {
            return {
                key: reminder.key,
                text: reminder.text,
            };
        });

        options.push(
            {
                key: 'divider_1',
                text: '-',
                itemType: DropdownMenuItemType.Divider,
            },
            {
                key: this.CUSTOM_EMAIL_REMINDER_KEY,
                text: loc(customReminderLabel),
            }
        );
        return options;
    };

    /**
     * Callback issued when the menu item is invoked
     */
    private onDropdownOptionChange = (
        ev: React.FormEvent<HTMLDivElement>,
        option?: IDropdownOption,
        index?: number
    ) => {
        if (option.key === this.CUSTOM_EMAIL_REMINDER_KEY) {
            setDraftInboxReminderOffset(null);
            setCustomReminderDate(this.props.event.Start);
        } else {
            // Otherwise select the email reminder in the dropdown
            setDraftInboxReminderOffset(+option.key);
        }
    };

    private renderSendOptionSection = (): JSX.Element => {
        const inboxReminderDraft = getInboxReminderDraft();
        return (
            <Checkbox
                checked={isSendOptionToAllAttendees(inboxReminderDraft.SendOption)}
                className={styles.checkbox}
                defaultChecked={isSendOptionToAllAttendees(inboxReminderDraft.SendOption)}
                label={loc(sendToAllAttendees)}
                onChange={this.onSendOptionChanged}
            />
        );
    };

    private onReminderMessageChanged(
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue: string
    ) {
        setDraftInboxReminderMessage(newValue);
    }

    private onSendOptionChanged(event: React.FormEvent<HTMLInputElement>, checked: boolean) {
        const isSendToAllAttendees = checked;
        setDraftInboxReminderSendOption(
            isSendToAllAttendees
                ? EmailReminderSendOption.AllAttendees
                : EmailReminderSendOption.User
        );
    }

    private renderSaveDiscardButtons = (): JSX.Element => {
        return (
            <div className={styles.buttonsContainer}>
                <PrimaryButton
                    className={styles.saveButton}
                    onClick={this.onSaveClicked}
                    text={loc(saveEmailRemidnerText)}
                />
                <DefaultButton
                    className={styles.discardButton}
                    onClick={this.onDiscardClicked}
                    text={loc(dicardEmailReminderText)}
                />
            </div>
        );
    };

    private onSaveClicked = () => {
        saveDraftInboxReminder(this.props.event, this.props.entrySource);
    };

    private onDiscardClicked() {
        discardDraftInboxReminder();
    }

    private shouldRenderSendToAllAttendees = (): boolean => {
        return this.props.event.IsOrganizer && this.getNonOrganizerAttendeeCount() > 0;
    };

    private shouldShowCustomReminderDatePickers(): boolean {
        return getCustomReminderDate() !== null;
    }

    private getNonOrganizerAttendeeCount = (): number => {
        const requiredAttendees = this.props.event.RequiredAttendees;
        const optionalAttendees = this.props.event.OptionalAttendees;

        const requiredAttendeeCount = !requiredAttendees
            ? 0
            : requiredAttendees.filter(
                  attendee =>
                      this.getAttendeeMailbox(attendee) !=
                      this.getAttendeeMailbox(this.props.event.Organizer)
              ).length;
        const optionalAttendeeCount = !optionalAttendees
            ? 0
            : optionalAttendees.filter(
                  attendee =>
                      this.getAttendeeMailbox(attendee) !=
                      this.getAttendeeMailbox(this.props.event.Organizer)
              ).length;

        return requiredAttendeeCount + optionalAttendeeCount;
    };

    private getAttendeeMailbox(attendee: AttendeeType): string {
        return attendee?.Mailbox ? attendee.Mailbox.EmailAddress : null;
    }
}
